import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "../components/AdminLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Filter, Eye, Download, FileText, MessageCircle, User, Check, X, AlertTriangle, CheckSquare, Square, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function generateFinancialYears(count = 3) {
  const years = [];
  const today = new Date();

  let startYear =
    today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;

  for (let i = 0; i < count; i++) {
    const start = startYear - i;
    const end = start + 1;
    years.push(`${start}-${end}`);
  }

  return years;
}

const AdminRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignedToFilter, setAssignedToFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState("details");
  
  // Document review state
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docStatus, setDocStatus] = useState("");
  const [docNotes, setDocNotes] = useState("");
  const [updatingDoc, setUpdatingDoc] = useState(false);

  const [me, setMe] = useState(null);
  const [caAdmins, setCaAdmins] = useState([]);


  const [financialYear, setFinancialYear] = useState("current"); // empty means "current FY" default by backend
  const fyOptions = generateFinancialYears(3);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [applyingBulk, setApplyingBulk] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [total, setTotal] = useState(0);



  const totalPages = Math.max(1, Math.ceil((total || 0) / limit));

  useEffect(() => {
    fetchRequests();

    const init = async () => {
    try {
        const meRes = await api.get("/auth/me");
        setMe(meRes.data);

        if (meRes.data?.admin_role === "super_admin") {
          const adminsRes = await api.get("/admin/admins");
          // only CA admins
          setCaAdmins((adminsRes.data || []).filter(a => a.admin_role === "ca_admin" && a.is_active !== false));
        }
      } catch (e) {
        // ignore
      }
    };

    init();

  }, []);

  // When FY, status, or assignment changes: reset to page 1 and refetch
  useEffect(() => {
    setLoading(true);
    setPage(1);
    fetchRequests(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialYear, statusFilter, assignedToFilter]);

  // When search changes: debounce, reset to page 1 and refetch
  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      setPage(1);
      fetchRequests(1);
    }, 400);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchRequests = async (pageArg = page) => {
    try {
      const params = {
        page: pageArg,
        limit,
        financial_year: financialYear === "current" ? undefined : financialYear,
        status: statusFilter !== "all" ? statusFilter : undefined,
        assigned_to: me?.admin_role === "super_admin" && assignedToFilter !== "all"
          ? assignedToFilter
          : undefined,
        q: searchTerm?.trim() ? searchTerm.trim() : undefined,
      };

      const res = await api.get("/admin/requests", { params });
      const data = res.data;
      
      // ✅ If backend returns array (old behavior)
      if (Array.isArray(data)) {
        setRequests(data);
        setTotal(data.length);
        setPage(1);
        return;
      }

      // ✅ If backend returns paginated object (new behavior)
      setRequests(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || pageArg);
    } catch (err) {
      console.error("Failed to load requests", err);
      toast.error(err?.response?.data?.detail || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (requestId, adminIdOrNull) => {
  try {
    await api.put(`/admin/requests/${requestId}/assign`, {
      admin_id: adminIdOrNull || null
    });
    toast.success("Assignment updated");
    fetchRequests();
    if (selectedRequest?.id === requestId) {
      setSelectedRequest({
        ...selectedRequest,
        assigned_to_admin_id: adminIdOrNull || null,
        assigned_to_admin_name: adminIdOrNull
          ? (caAdmins.find(a => a.id === adminIdOrNull)?.name || caAdmins.find(a => a.id === adminIdOrNull)?.email)
          : null
      });
    }
  } catch (err) {
    toast.error(err.response?.data?.detail || "Failed to assign");
  }
};

  const handleViewDetails = async (request) => {
    setSelectedRequest(request);
    setActiveTab("details");
    try {
      const [docsRes, msgsRes] = await Promise.all([
        api.get(`/requests/${request.id}/documents`),
        api.get(`/messages/conversation/${request.user_id}`)
      ]);
      setDocuments(docsRes.data);
      setMessages(msgsRes.data);
    } catch (err) {
      setDocuments([]);
      setMessages([]);
    }
  };

  const handleUpdateStatus = async (requestId, status) => {
    try {
      await api.put(`/admin/requests/${requestId}/status?status=${status}`);
      toast.success("Status updated");
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status });
      }
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const res = await api.get(`/documents/${docId}/download`);

      if (res.data.download_url) {
        const link = document.createElement("a");
        link.href = res.data.download_url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.download = res.data.file_name || fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (res.data.file_data) {
        const link = document.createElement("a");
        link.href = `data:application/octet-stream;base64,${res.data.file_data}`;
        link.download = res.data.file_name || fileName;
        link.click();
      } else {
        throw new Error("No downloadable file returned");
      }

      toast.success("Download started");
    } catch (err) {
      toast.error("Failed to download document");
    }
  };

  const handleReviewDoc = (doc) => {
    setSelectedDoc(doc);
    setDocStatus(doc.status);
    setDocNotes(doc.admin_notes || "");
  };

  const handleUpdateDocStatus = async () => {
    if (!docStatus) return;
    setUpdatingDoc(true);
    try {
      await api.put(`/admin/documents/${selectedDoc.id}/status`, {
        status: docStatus,
        admin_notes: docNotes
      });
      toast.success("Document status updated");
      setSelectedDoc(null);
      // Refresh documents
      const docsRes = await api.get(`/requests/${selectedRequest.id}/documents`);
      setDocuments(docsRes.data);
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingDoc(false);
    }
  };

  // --- CSV Export ---
  const exportToCSV = (data, filename) => {
    const headers = ["Name","Email","Plan","Financial Year","Status","Payment Status","Amount","Date Created"];
    const rows = data.map(r => [
      r.user_name,
      r.user_email,
      r.plan_name,
      r.financial_year,
      r.status.replaceAll("_"," "),
      r.payment_status,
      r.price != null ? r.price : "",
      new Date(r.created_at).toLocaleDateString("en-IN")
    ]);
    const csv = [headers, ...rows].map(row =>
      row.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAll = () => {
    if (!requests.length) { toast.error("No data to export"); return; }
    exportToCSV(requests, `mooltax_requests_${new Date().toISOString().slice(0,10)}.csv`);
    toast.success(`Exported ${requests.length} requests`);
  };

  const handleExportFiltered = () => {
    if (!filteredRequests.length) { toast.error("No filtered data to export"); return; }
    exportToCSV(filteredRequests, `mooltax_requests_filtered_${new Date().toISOString().slice(0,10)}.csv`);
    toast.success(`Exported ${filteredRequests.length} requests`);
  };

  // --- Bulk Select ---
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredRequests.map(r => r.id);
    const allSelected = visibleIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => { const next = new Set(prev); visibleIds.forEach(id => next.delete(id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); visibleIds.forEach(id => next.add(id)); return next; });
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!selectedIds.size || !bulkStatus) return;
    if (!window.confirm(`Update ${selectedIds.size} case(s) to "${bulkStatus.replaceAll("_"," ")}"?`)) return;
    setApplyingBulk(true);
    try {
      const res = await api.post("/admin/requests/bulk-status", {
        request_ids: Array.from(selectedIds),
        status: bulkStatus
      });
      toast.success(res.data.message);
      setSelectedIds(new Set());
      setBulkStatus("");
      fetchRequests();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    } finally {
      setApplyingBulk(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = req.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          req.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          req.plan_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || req.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statuses = ["pending", "documents_uploaded", "under_review", "completed", "rejected", "tax_filed", "closed"];

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <Check size={14} className="text-green-600" />;
      case 'rejected': return <X size={14} className="text-red-600" />;
      case 'needs_revision': return <AlertTriangle size={14} className="text-orange-500" />;
      default: return null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>Tax Filing Requests</h1>
          <p className="opacity-70 mt-1">Manage and track all client requests</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" size={20} />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or plan..."
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={financialYear} onValueChange={setFinancialYear}>
            <SelectTrigger className="w-48" data-testid="fy-filter">
              <SelectValue placeholder="Financial Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Current FY</SelectItem>
              {fyOptions.map((fy) => (
                <SelectItem key={fy} value={fy}>
                  {fy}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <Filter size={16} className="mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {statuses.map((s) => (
                <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {me?.admin_role === "super_admin" && (
            <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
              <SelectTrigger className="w-48" data-testid="assigned-filter">
                <SelectValue placeholder="Assigned To" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignments</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {caAdmins.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name || a.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="empty-state bg-white rounded-xl border" style={{ borderColor: 'var(--border)' }}>
            <p>No requests found</p>
          </div>
        ) : (
          <>
            {/* Action bar — export + bulk */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl border px-4 py-3"
              style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-3 flex-wrap">
                {selectedIds.size > 0 ? (
                  <>
                    <span className="text-sm font-medium">{selectedIds.size} selected</span>
                    <Select value={bulkStatus} onValueChange={setBulkStatus}>
                      <SelectTrigger className="w-44 h-8 text-xs">
                        <SelectValue placeholder="Change status to..." />
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.map(s => (
                          <SelectItem key={s} value={s} className="text-xs">{s.replaceAll("_"," ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      disabled={!bulkStatus || applyingBulk}
                      onClick={handleBulkStatusUpdate}
                      className="btn-primary h-8 text-xs"
                      data-testid="bulk-apply-btn"
                    >
                      {applyingBulk ? "Applying..." : "Apply"}
                    </Button>
                    <button
                      className="text-xs opacity-50 underline"
                      onClick={() => setSelectedIds(new Set())}
                    >
                      Clear selection
                    </button>
                  </>
                ) : (
                  <span className="text-sm opacity-60">
                    {filteredRequests.length} of {total} requests
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleExportFiltered}
                  className="h-8 text-xs gap-1" data-testid="export-filtered-btn">
                  <Download size={13} /> Export filtered
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportAll}
                  className="h-8 text-xs gap-1" data-testid="export-all-btn">
                  <Download size={13} /> Export all
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <button onClick={toggleSelectAll} className="opacity-60 hover:opacity-100">
                          {filteredRequests.every(r => selectedIds.has(r.id)) && filteredRequests.length > 0
                            ? <CheckSquare size={16} />
                            : <Square size={16} />}
                        </button>
                      </th>
                      <th>Client</th>
                      <th>Plan</th>
                      <th>FY</th>
                      <th>Status</th>
                      {me?.admin_role === "super_admin" && <th>Assigned To</th>}
                      <th>Payment</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((req) => (
                      <tr key={req.id} data-testid={`request-row-${req.id}`}
                        style={{ background: selectedIds.has(req.id) ? 'var(--color-background-secondary, #f9f9f7)' : undefined }}>
                        <td style={{ width: 40 }}>
                          <button
                            onClick={() => toggleSelect(req.id)}
                            className="opacity-50 hover:opacity-100"
                            data-testid={`select-${req.id}`}
                          >
                            {selectedIds.has(req.id) ? <CheckSquare size={15} /> : <Square size={15} />}
                          </button>
                        </td>
                        <td style={{ minWidth: 160 }}>
                          <p className="font-medium" style={{ whiteSpace: 'nowrap' }}>{req.user_name}</p>
                          <p className="text-xs opacity-60" style={{ whiteSpace: 'nowrap' }}>{req.user_email}</p>
                        </td>
                        <td style={{ minWidth: 140 }}>
                          <p className="text-sm font-medium">{req.plan_name}</p>
                          <Badge variant="outline" className="text-xs capitalize">{req.plan_type}</Badge>
                        </td>
                        <td className="mono text-sm" style={{ whiteSpace: 'nowrap' }}>{req.financial_year}</td>
                        <td style={{ minWidth: 150 }}>
                          <Select value={req.status} onValueChange={(v) => handleUpdateStatus(req.id, v)}>
                            <SelectTrigger className="w-36 h-8 text-xs" data-testid={`status-select-${req.id}`}>
                              <span className={`status-${req.status} px-1.5 py-0.5 rounded text-xs`}>
                                {req.status.replaceAll('_', ' ')}
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              {statuses.map((s) => (
                                <SelectItem key={s} value={s} className="text-xs">{s.replaceAll('_', ' ')}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>

                        {me?.admin_role === "super_admin" && (
                          <td style={{ minWidth: 150 }}>
                            <Select
                              value={req.assigned_to_admin_id || "unassigned"}
                              onValueChange={(v) => handleAssign(req.id, v === "unassigned" ? null : v)}
                            >
                              <SelectTrigger className="w-36 h-8 text-xs">
                                <SelectValue placeholder="Assign..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                                {caAdmins.map((a) => (
                                  <SelectItem key={a.id} value={a.id} className="text-xs">
                                    {a.name || a.email}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        )}

                        <td>
                          <Badge className={`status-${req.payment_status} text-xs capitalize`}>
                            {req.payment_status}
                          </Badge>
                        </td>
                        <td className="mono text-sm font-medium" style={{ color: 'var(--accent)', whiteSpace: 'nowrap' }}>
                          {me?.admin_role === "ca_admin" || req.price == null ? "—" : `₹${req.price.toLocaleString()}`}
                        </td>

                        <td className="text-xs opacity-60" style={{ whiteSpace: 'nowrap' }}>
                          {new Date(req.created_at).toLocaleDateString('en-IN')}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(req)}
                            data-testid={`view-request-${req.id}`}
                          >
                            <Eye size={16} className="mr-1" /> View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                disabled={page <= 1}
                onClick={() => {
                  setLoading(true);
                  fetchRequests(page - 1);
                }}
                data-testid="page-prev"
              >
                Prev
              </Button>

              <span className="text-sm opacity-70" data-testid="page-indicator">
                Page {page} / {totalPages}
              </span>

              <Button
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => {
                  setLoading(true);
                  fetchRequests(page + 1);
                }}
                data-testid="page-next"
              >
                Next
              </Button>
            </div>
          </>
        )}
          
      </div>

      {/* Request Details Dialog with Tabs */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif' }}>
              Case Details - {selectedRequest?.user_name}
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
                <TabsTrigger value="messages">Messages ({messages.length})</TabsTrigger>
              </TabsList>
              
              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                    <p className="text-sm opacity-60">Client Name</p>
                    <p className="font-medium">{selectedRequest.user_name}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                    <p className="text-sm opacity-60">Email</p>
                    <p className="font-medium">{selectedRequest.user_email}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                    <p className="text-sm opacity-60">Plan</p>
                    <p className="font-medium">{selectedRequest.plan_name}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                    <p className="text-sm opacity-60">Financial Year</p>
                    <p className="font-medium mono">{selectedRequest.financial_year}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                    <p className="text-sm opacity-60">Tax Regime</p>
                    <p className="font-medium">
                      {selectedRequest.tax_regime === "not_sure"
                        ? "Not Sure"
                        : selectedRequest.tax_regime
                          ? `${selectedRequest.tax_regime.charAt(0).toUpperCase() + selectedRequest.tax_regime.slice(1)} Regime`
                          : "-"}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                    <p className="text-sm opacity-60">Amount</p>
                    <p className="font-medium mono" style={{ color: 'var(--accent)' }}>
                      {me?.admin_role === "ca_admin" || selectedRequest.price == null ? "—" : `₹${selectedRequest.price.toLocaleString()}`}
                    </p>

                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                    <p className="text-sm opacity-60">Payment Status</p>
                    <Badge className={`status-${selectedRequest.payment_status}`}>{selectedRequest.payment_status}</Badge>
                  </div>
                  {me?.admin_role === "super_admin" && (
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                      <p className="text-sm opacity-60">Assigned To</p>
                      <p className="font-medium">
                        {selectedRequest.assigned_to_admin_name || "Unassigned"}
                      </p>
                    </div>
                  )}

                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                  <p className="text-sm opacity-60 mb-2">Required Documents</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedRequest.required_documents?.map((doc, idx) => (
                      <Badge key={idx} variant="outline">{doc}</Badge>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="space-y-3 mt-4">
                {documents.length === 0 ? (
                  <p className="text-center py-8 opacity-60">No documents uploaded yet</p>
                ) : (
                  documents.map((doc) => (
                    <div key={doc.id} className="p-4 rounded-lg border" style={{ borderColor: 'var(--border)' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText size={24} style={{ color: 'var(--accent)' }} />
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              {doc.name}
                              {getStatusIcon(doc.status)}
                            </p>
                            <p className="text-sm opacity-60">{doc.document_type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`status-${doc.status}`}>{doc.status.replace('_', ' ')}</Badge>
                          <Button variant="outline" size="sm" onClick={() => handleDownload(doc.id, doc.file_name)}>
                            <Download size={16} />
                          </Button>
                          <Button size="sm" className="btn-primary" onClick={() => handleReviewDoc(doc)}>
                            Review
                          </Button>
                        </div>
                      </div>
                      {doc.admin_notes && (
                        <div className="mt-2 p-2 rounded text-sm" style={{ backgroundColor: 'var(--secondary)' }}>
                          <strong>Notes:</strong> {doc.admin_notes}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </TabsContent>

              {/* Messages Tab */}
              <TabsContent value="messages" className="mt-4">
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {messages.length === 0 ? (
                    <p className="text-center py-8 opacity-60">No messages yet</p>
                  ) : (
                    messages.map((msg) => (
                      <div 
                        key={msg.id} 
                        className={`p-3 rounded-lg ${msg.sender_type === 'admin' ? 'ml-8' : 'mr-8'}`}
                        style={{ backgroundColor: msg.sender_type === 'admin' ? 'var(--primary)' : 'var(--secondary)', color: msg.sender_type === 'admin' ? 'white' : 'inherit' }}
                      >
                        <p className="text-sm font-medium mb-1">{msg.sender_name} ({msg.sender_type})</p>
                        <p className="text-sm">{msg.content}</p>
                        <p className="text-xs opacity-60 mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Review Sub-Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Document</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                <p className="font-medium">{selectedDoc.name}</p>
                <p className="text-sm opacity-60">{selectedDoc.document_type}</p>
              </div>
              <Button variant="outline" className="w-full" onClick={() => handleDownload(selectedDoc.id, selectedDoc.file_name)}>
                <Download size={16} className="mr-2" /> Download Document
              </Button>
              <div>
                <label className="text-sm font-medium">Update Status</label>
                <Select value={docStatus} onValueChange={setDocStatus}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="needs_revision">Needs Revision</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  value={docNotes}
                  onChange={(e) => setDocNotes(e.target.value)}
                  placeholder="Add notes for the client..."
                  className="mt-2"
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedDoc(null)}>Cancel</Button>
            <Button onClick={handleUpdateDocStatus} disabled={updatingDoc} className="btn-primary">
              {updatingDoc ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminRequests;